import { FC, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { Controller, useForm, useWatch, FormProvider } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { Flex, Box } from '@rebass/emotion';

import { H3 } from '@app/shop/components/typography';
import { Button } from '@app/shop/components/Button';

import CustomerAddresses from './CustomerAddresses/CustomerAddresses';
import { ADD_ADDRESS } from './CustomerAddresses/CustomerAddresses.queries';
import { mapToNewCustomerAddress } from './helpers';
import AddressForm from './AddressForm/AddressForm';
import { SET_BILLING_ADDRESS } from './Address.queries';
import {
  AddressFormFields,
  AddressFormNamespaces,
  FORM_REVALIDATE_MODE,
} from './Address.constants';
import messages from './Address.messages';
import { AddressFormState } from './Address.types';

import {
  CreateCustomerAddressMutation,
  CreateCustomerAddressMutationVariables,
} from './CustomerAddresses/__generated__/CreateCustomerAddressMutation';
import {
  SetBillingAddress,
  SetBillingAddressVariables,
} from './__generated__/SetBillingAddress';

const BillingAddress: FC<{ cartId: string }> = ({ cartId }) => {
  const history = useHistory();
  const { language = 'en' } = useParams<{ language?: string }>();
  const form = useForm<AddressFormState>({
    reValidateMode: FORM_REVALIDATE_MODE,
  });

  const [setBillingAddress, { loading: isBillingAddressSending }] = useMutation<
    SetBillingAddress,
    SetBillingAddressVariables
  >(SET_BILLING_ADDRESS);

  const [
    createCustomerAddress,
    { loading: isCustomerAddressSending },
  ] = useMutation<
    CreateCustomerAddressMutation,
    CreateCustomerAddressMutationVariables
  >(ADD_ADDRESS);

  const isAddressFormVisible = useWatch({
    control: form.control,
    name: AddressFormFields.FormVisible,
    defaultValue: false,
  });

  const submit = useCallback(
    async ({ billing, selected }: AddressFormState) => {
      let customerAddressId: number | null | undefined =
        !billing && !!selected?.id ? selected?.id : undefined;

      if (!customerAddressId) {
        const newCustomerAddress = mapToNewCustomerAddress(billing || selected);

        if (!newCustomerAddress) {
          throw new Error(
            'Could not create customer address from given billing address form'
          );
        }

        const { data: customerAddressData } = await createCustomerAddress({
          variables: { input: newCustomerAddress },
        });

        customerAddressId =
          customerAddressData?.magento2?.createCustomerAddress?.id;

        if (!customerAddressId) {
          throw new Error('Could not find new customer address ID for billing');
        }
      }

      await setBillingAddress({
        variables: { cartId, billingAddressId: customerAddressId },
      });

      history.push(
        generatePath('/:language/shop/checkout/review/', { language })
      );
    },
    [setBillingAddress, cartId, history, language, createCustomerAddress]
  );

  const isLoading = isCustomerAddressSending || isBillingAddressSending;

  return (
    <FormProvider {...form}>
      <H3 as="h2">
        <FormattedMessage {...messages['billingAddressTitle']} />
      </H3>

      <Controller
        control={form.control}
        name={AddressFormFields.FormVisible}
        defaultValue={false}
        render={({ onChange }) => (
          <input
            name={AddressFormFields.FormVisible}
            type="hidden"
            onChange={(e) => {
              onChange(e.target.value === 'false' ? false : true);
            }}
          />
        )}
      />

      <Flex my={20} flexWrap="wrap">
        <CustomerAddresses />

        <Box width={1} mb={20}>
          {isAddressFormVisible && (
            <AddressForm namespace={AddressFormNamespaces.Billing} />
          )}
        </Box>

        <Box width={1}>
          <Flex justifyContent="flex-end">
            <Button
              data-testid="address-continue-button"
              type="primary"
              onClick={form.handleSubmit(submit)}
              isLoading={isLoading}
              disabled={isLoading}
            >
              <FormattedMessage {...messages.continueButtonLabel} />
            </Button>
          </Flex>
        </Box>
      </Flex>
    </FormProvider>
  );
};

export default BillingAddress;
